/**
 * useBuilderChrome — Manages all UI "chrome" state for the PageBuilder.
 *
 * This includes:
 *   - Modal visibility (open registry pattern instead of 12+ booleans)
 *   - Panel widths and resize handling
 *   - Zoom level (keyboard, trackpad pinch, fit-to-window)
 *   - Placement mode (click-to-place components)
 *
 * None of this state affects document data or history — it's purely UI.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BREAKPOINTS, DESKTOP_CANVAS_WIDTH } from '../BreakpointToggle';

// ---------------------------------------------------------------------------
// Modal registry helper
// ---------------------------------------------------------------------------

/**
 * Creates a modal state entry with open/close/toggle and optional payload.
 *
 * The first argument is the current stored value for this modal key.
 * From it we derive:
 *   - isOpen: boolean flag indicating visibility
 *   - data:   payload value for payload-backed modals, or null for booleans
 *
 * close() and toggle() are type-aware: booleans reset to false, arrays to [],
 * objects/null to null — so the stored type never accidentally becomes boolean.
 *
 * Usage:
 *   modals.templateLibrary.open()
 *   modals.exportSection.open(sectionObj)  // payload stored in .data
 *   modals.exportSection.close()
 *   modals.exportSection.isOpen            // boolean
 *   modals.exportSection.data              // the sectionObj passed to open()
 */
function createModalEntry(value, setFn, key) {
  const isArray = Array.isArray(value);
  const isBool = typeof value === 'boolean';

  const isOpen = isBool
    ? value
    : isArray
      ? value.length > 0
      : value !== null && value !== undefined;

  const data = isBool ? null : value;

  return {
    isOpen,
    data,
    open: (payload) =>
      setFn((prev) => {
        // If a payload is provided, store it directly.
        if (payload !== undefined) return { ...prev, [key]: payload };
        // For boolean modals, set true. For payload modals, no-op (can't fabricate a payload).
        if (typeof prev[key] === 'boolean') return { ...prev, [key]: true };
        return prev;
      }),
    close: () =>
      setFn((prev) => {
        const cur = prev[key];
        if (typeof cur === 'boolean') return { ...prev, [key]: false };
        if (Array.isArray(cur)) return { ...prev, [key]: [] };
        return { ...prev, [key]: null };
      }),
    toggle: () =>
      setFn((prev) => {
        const cur = prev[key];
        // Only boolean modals support toggle — payload modals are ambiguous.
        if (typeof cur === 'boolean') return { ...prev, [key]: !cur };
        return prev;
      }),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const DEFAULT_MODAL_STATE = {
  customHtmlEditor: false,
  layersPalette: false,
  templateLibrary: false,
  publishPopover: false,
  deployPanel: false,
  exportSection: null, // null = closed, object = open with section data
  importModal: false,
  incompatibleSections: [], // empty = closed, non-empty = open
  mediaLibrary: false,
};

export default function useBuilderChrome({ breakpoint } = {}) {
  // -------------------------------------------------------------------------
  // Modals — single state object instead of 12 separate useState hooks
  // -------------------------------------------------------------------------
  const [modalState, setModalState] = useState(DEFAULT_MODAL_STATE);

  const modals = {
    customHtmlEditor: createModalEntry(modalState.customHtmlEditor, setModalState, 'customHtmlEditor'),
    layersPalette: createModalEntry(modalState.layersPalette, setModalState, 'layersPalette'),
    templateLibrary: createModalEntry(modalState.templateLibrary, setModalState, 'templateLibrary'),
    publishPopover: createModalEntry(modalState.publishPopover, setModalState, 'publishPopover'),
    deployPanel: createModalEntry(modalState.deployPanel, setModalState, 'deployPanel'),
    exportSection: createModalEntry(modalState.exportSection, setModalState, 'exportSection'),
    importModal: createModalEntry(modalState.importModal, setModalState, 'importModal'),
    incompatibleSections: createModalEntry(modalState.incompatibleSections, setModalState, 'incompatibleSections'),
    mediaLibrary: createModalEntry(modalState.mediaLibrary, setModalState, 'mediaLibrary'),
  };

  // Convenience: reset all modals (e.g. on page change)
  const resetModals = useCallback(() => {
    setModalState(DEFAULT_MODAL_STATE);
  }, []);

  // -------------------------------------------------------------------------
  // Layers palette target (which section/parent/index to add to)
  // -------------------------------------------------------------------------
  const [layersPaletteTarget, setLayersPaletteTarget] = useState(null);

  // -------------------------------------------------------------------------
  // Panel widths + resize
  // -------------------------------------------------------------------------
  const [panelWidths, setPanelWidths] = useState({ left: 18, center: 64, right: 18 });
  const [isResizing, setIsResizing] = useState(null);

  const handleMouseDown = useCallback((divider) => {
    setIsResizing(divider);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsResizing(null);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleGlobalMouseMove = (e) => {
      const mainContainer = document.querySelector('.resize-container');
      if (!mainContainer) return;

      const rect = mainContainer.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const totalWidth = rect.width;
      const percentage = (x / totalWidth) * 100;

      if (isResizing === 'left') {
        const newLeft = Math.max(12, Math.min(35, percentage));
        const remaining = 100 - newLeft;
        setPanelWidths((prev) => {
          const currentRightPct = (prev.right / (prev.center + prev.right)) * remaining;
          return { left: newLeft, center: remaining - currentRightPct, right: currentRightPct };
        });
      } else if (isResizing === 'right') {
        const newRight = Math.max(12, Math.min(35, 100 - percentage));
        const remaining = 100 - newRight;
        setPanelWidths((prev) => {
          const currentLeftPct = (prev.left / (prev.left + prev.center)) * remaining;
          return { left: currentLeftPct, center: remaining - currentLeftPct, right: newRight };
        });
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { passive: true });

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseUp]);

  // -------------------------------------------------------------------------
  // Zoom
  // -------------------------------------------------------------------------
  const [zoomLevel, setZoomLevel] = useState(100);

  // Keyboard + trackpad zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        setZoomLevel((prev) => Math.min(200, prev + 10));
      } else if (isMod && e.key === '-') {
        e.preventDefault();
        setZoomLevel((prev) => Math.max(25, prev - 10));
      } else if (isMod && e.key === '0') {
        e.preventDefault();
        setZoomLevel(100);
      }
    };

    const handleWheel = (e) => {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        const zoomSensitivity = 0.01;
        const zoomFactor = 1 - e.deltaY * zoomSensitivity;
        const clampedFactor = Math.max(0.9, Math.min(1.1, zoomFactor));
        setZoomLevel((prev) => Math.min(200, Math.max(25, Math.round(prev * clampedFactor))));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    const previewPanel = document.querySelector('[data-preview-canvas]');
    if (previewPanel) {
      previewPanel.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (previewPanel) {
        previewPanel.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  // Fit-to-window
  const handleFitToWindow = useCallback(() => {
    const previewPanel = document.querySelector('[data-preview-canvas]');
    if (previewPanel) {
      const availableWidth = previewPanel.clientWidth - 64;
      const canvasWidth =
        breakpoint === 'desktop'
          ? DESKTOP_CANVAS_WIDTH
          : BREAKPOINTS.find((b) => b.id === breakpoint)?.width || 375;
      const fitZoom = Math.floor((availableWidth / canvasWidth) * 100);
      setZoomLevel(Math.min(100, Math.max(25, fitZoom)));
    }
  }, [breakpoint]);

  // Auto-fit on breakpoint change
  useEffect(() => {
    const timer = setTimeout(() => {
      handleFitToWindow();
    }, 100);
    return () => clearTimeout(timer);
  }, [breakpoint, handleFitToWindow]);

  // -------------------------------------------------------------------------
  // Placement mode
  // -------------------------------------------------------------------------
  const [placementMode, setPlacementMode] = useState(null);
  const placementModeRef = useRef(null);

  // Keep ref in sync
  useEffect(() => {
    placementModeRef.current = placementMode;
  }, [placementMode]);

  const startPlacement = useCallback((componentType, componentData, placementLevel) => {
    setPlacementMode({ componentType, componentData, placementLevel });
  }, []);

  const cancelPlacement = useCallback(() => {
    placementModeRef.current = null;
    setPlacementMode(null);
  }, []);

  // -------------------------------------------------------------------------
  // Publishing state (UI-only)
  // -------------------------------------------------------------------------
  const [publishing, setPublishing] = useState(false);
  const [siteData, setSiteData] = useState(null);

  // -------------------------------------------------------------------------
  // Return value
  // -------------------------------------------------------------------------
  return {
    // Modals
    modals,
    modalState,
    setModalState,
    resetModals,
    layersPaletteTarget,
    setLayersPaletteTarget,

    // Panel resize
    panelWidths,
    setPanelWidths,
    isResizing,
    handleMouseDown,
    handleMouseUp,

    // Zoom
    zoomLevel,
    setZoomLevel,
    handleFitToWindow,

    // Placement
    placementMode,
    setPlacementMode,
    placementModeRef,
    startPlacement,
    cancelPlacement,

    // Publishing
    publishing,
    setPublishing,
    siteData,
    setSiteData,
  };
}
